/**
 * Token Status Hook
 *
 * Performs proactive token validation before widgets mount.
 * Checks token expiration (with buffer) and scope sufficiency.
 *
 * This is the "pre-mount" layer of the two-layer interception pattern.
 *
 * @see .work/specs/M9/005-widget-platform-integration.md
 */
'use client';

import { useMemo } from 'react';

import {
  useListProviderAccounts,
  type ProviderAccount,
} from '@/src/hooks/useProviderAccounts';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

/** Buffer time before expiry to treat token as expired (5 minutes) */
const EXPIRY_BUFFER_MS = 5 * 60 * 1000;

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface TokenStatusResult {
  /** True when token is expired or within buffer of expiry */
  isExpired: boolean;
  /** True when required scopes are not present */
  isMissingScopes: boolean;
  /** List of missing scope identifiers */
  missingScopes: string[];
  /** URL for re-authentication */
  reconnectUrl: string;
  /** URL for scope upgrade */
  upgradeUrl: string;
  /** True while fetching account data */
  isLoading: boolean;
  /** Account display name for error messages */
  accountName: string;
  /** Provider identifier for branding */
  provider: string;
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

/**
 * Hook to check token status for an account before widget mount.
 *
 * Uses cached query data to avoid extra API calls.
 * Completes within 50ms when data is cached.
 *
 * @param accountId - The provider account ID to check
 * @param requiredScopes - Array of required scope identifiers
 * @returns Token status with expiry and scope validation results
 *
 * @example
 * function WidgetLoader({ accountId, manifest }) {
 *   const { isExpired, isMissingScopes, reconnectUrl } = useTokenStatus(
 *     accountId,
 *     manifest.scopes ?? []
 *   );
 *
 *   if (isExpired || isMissingScopes) {
 *     reportError(accountId, createTokenError(...));
 *     return <WidgetPlaceholder />;
 *   }
 *
 *   return <Widget />;
 * }
 */
export function useTokenStatus(
  accountId: string,
  requiredScopes: string[]
): TokenStatusResult {
  // Use existing cached query data
  const { data, isLoading } = useListProviderAccounts({});

  return useMemo(() => {
    // Find account in cached data
    const account = data?.items.find(
      (a: ProviderAccount) => a.id === accountId
    );

    // Default result when loading or account not found
    if (isLoading || !account) {
      return {
        isExpired: false,
        isMissingScopes: false,
        missingScopes: [],
        reconnectUrl: '',
        upgradeUrl: '',
        isLoading,
        accountName: '',
        provider: '',
      };
    }

    // Check token expiration with buffer
    const isExpired = checkTokenExpired(account.tokenExpiresAt);

    // Check scope sufficiency
    const missingScopes = requiredScopes.filter(
      scope => !account.scopes.includes(scope)
    );
    const isMissingScopes = missingScopes.length > 0;

    // Generate OAuth URLs
    const reconnectUrl = `/api/oauth/${account.provider}/reconnect?accountId=${accountId}`;
    const upgradeUrl = isMissingScopes
      ? `/api/oauth/${account.provider}/upgrade?accountId=${accountId}&scopes=${missingScopes.join(',')}`
      : '';

    return {
      isExpired,
      isMissingScopes,
      missingScopes,
      reconnectUrl,
      upgradeUrl,
      isLoading: false,
      accountName: account.displayName,
      provider: account.provider,
    };
  }, [accountId, data?.items, isLoading, requiredScopes]);
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/**
 * Check if token is expired or within buffer of expiry.
 *
 * Exported for direct testing of the fail-closed corrupt-timestamp branch.
 */
export function checkTokenExpired(tokenExpiresAt: string | null): boolean {
  if (!tokenExpiresAt) {
    // No expiry means token doesn't expire (or we don't know)
    return false;
  }

  const expiresAtMs = new Date(tokenExpiresAt).getTime();

  // Fail CLOSED on an unparseable timestamp: an invalid date yields NaN, and
  // every `>=` comparison against NaN is false, so a corrupt token would
  // otherwise slip through this pre-mount gate as "not expired" (AUD4-018).
  if (Number.isNaN(expiresAtMs)) {
    return true;
  }

  const nowMs = Date.now();

  // Token is considered expired if current time is within buffer of expiry
  return nowMs >= expiresAtMs - EXPIRY_BUFFER_MS;
}
